import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../utils/api";

/**
 * 第三方 / 干跑认证回跳页。
 * 查询参数：provider, kind, request_id, user_id
 */
export default function VerificationCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const provider = params.get("provider") || "";
  const kind = params.get("kind") || "real_name";
  const requestId = params.get("request_id") || params.get("BizToken") || "";
  const userId =
    params.get("user_id") || localStorage.getItem("user_id") || "";

  const [hint, setHint] = useState("正在处理认证结果…");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const title = useMemo(() => {
    if (kind === "face") return "真人认证回跳";
    return "实名认证回跳";
  }, [kind]);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const finish = (ok, message) => {
      if (cancelled) return;
      setDone(true);
      setHint(message);
      if (!ok) setError(message);
    };

    const pollResult = async (rid) => {
      const path =
        kind === "face"
          ? `/api/verification/face/result?user_id=${userId}&request_id=${encodeURIComponent(rid)}`
          : `/api/verification/real-name/result?user_id=${userId}&request_id=${encodeURIComponent(rid)}`;
      let attempts = 0;
      const tick = async () => {
        if (cancelled) return;
        attempts += 1;
        try {
          const res = await apiFetch(path);
          const data = await res.json();
          if (data.status === "success") {
            finish(
              true,
              kind === "face" ? "🎉 真人认证已完成" : "🎉 实名认证已完成"
            );
            return;
          }
          if (data.status === "failed") {
            finish(false, data.message || "认证未通过");
            return;
          }
          if (attempts >= 12) {
            finish(false, "仍在处理中，请返回认证中心稍后刷新");
            return;
          }
          setHint(`认证处理中…（${attempts}/12）`);
          timer = setTimeout(tick, 2000);
        } catch (err) {
          finish(false, err.message || "查询失败");
        }
      };
      tick();
    };

    const run = async () => {
      if (!userId) {
        finish(false, "请先登录后再完成认证回跳");
        return;
      }

      // 干跑：在本页一键确认
      if (provider === "dry_run") {
        if (!requestId) {
          finish(false, "缺少 request_id");
          return;
        }
        try {
          const res = await apiFetch("/api/verification/dry-run/complete", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: Number(userId),
              request_id: requestId,
              kind,
              success: true,
            }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || "干跑确认失败");
          finish(
            data.status === "success",
            data.status === "success"
              ? kind === "face"
                ? "🎉 干跑真人认证成功"
                : "🎉 干跑实名认证成功"
              : data.message || "干跑失败"
          );
        } catch (err) {
          finish(false, err.message || "干跑失败");
        }
        return;
      }

      // 支付宝 / 腾讯：带回 request_id 或 BizToken 则轮询；否则提示回认证中心刷新
      if (requestId) {
        setHint("正在向第三方查询认证结果…");
        await pollResult(requestId);
        return;
      }

      finish(
        true,
        "已从第三方返回。若状态未更新，请回到认证中心点击「刷新认证状态」。"
      );
    };

    run();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [provider, kind, requestId, userId]);

  return (
    <div className="max-w-md mx-auto py-10 px-4 space-y-6">
      <header className="space-y-2">
        <p className="text-sm text-pink-500">认证回跳</p>
        <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
        <p className="text-sm text-gray-500">
          渠道：{provider || "第三方"}
        </p>
      </header>

      <div
        className={`rounded-2xl px-4 py-5 text-sm leading-7 ${
          error
            ? "bg-red-50 text-red-700"
            : done
            ? "bg-green-50 text-green-800"
            : "bg-slate-50 text-slate-700"
        }`}
      >
        {hint}
      </div>

      <div className="flex flex-wrap gap-3">
        <Link
          to="/center/verify"
          className="px-4 py-2 rounded-xl bg-pink-500 text-white text-sm"
        >
          返回认证中心
        </Link>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 rounded-xl border text-sm text-gray-600"
        >
          返回上一页
        </button>
      </div>
    </div>
  );
}
